// Public profile route. Sibling of the static `/profile` self-editor —
// TanStack Router routes static segments before dynamic ones, so `/profile`
// keeps the editor and any other path matches `$id`.

import { useState } from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { isUuid } from '@leanin/shared';
import { asUserId } from '@leanin/shared/ids';
import { useMember } from '@/lib/use-session';
import {
  mapProfileViewToProfile,
  memberSinceFromCreatedAt,
} from '@/components/profile/mapper';
import {
  ProfileChrome,
  type ProfileHeaderData,
} from '@/components/profile/ProfileChrome';
import { ProfileChromeSkeleton } from '@/components/profile/ProfileChromeSkeleton';
import { ProfileContentView } from '@/components/profile/ProfileContentView';
import { ErrorState, useDelayedLoading } from '@leanin/ui';
import { RetryButton } from '@/components/states/index';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import { isNotFoundApiError } from '@/lib/api-error';
import {
  fetchMember,
  fetchMemberMutuals,
  fetchIsFollowing,
  identityKeys,
} from '@/lib/queries/identity';
import {
  fetchMyCircles,
  fetchMyNetworks,
} from '@/lib/queries/groups';
import { feedKeys, fetchPostsByAuthor } from '@/lib/queries/feed';
import { authoredPostToFeedPost } from '@/components/feed/feed-item-to-post';
import { formatRelativeShort } from '@/lib/format-date';
import type { Profile } from '@/components/profile/types';

export const Route = createFileRoute('/_authed/profile/$id')({
  beforeLoad: ({ params }) => {
    if (!isUuid(params.id)) throw notFound();
  },
  component: PublicProfilePage,
});

function buildHeadline(data: {
  jobTitle?: string | null;
  company?: string | null;
  bio?: string | null;
}): string {
  const role = data.jobTitle ?? '';
  const company = data.company ?? '';
  if (role && company) return `${role} at ${company}`;
  return role || company || data.bio?.slice(0, 80) || '';
}

function PublicProfilePage() {
  const { id } = Route.useParams();
  const userId = asUserId(id);
  const viewer = useMember();

  const memberQuery = useQuery({
    queryKey: identityKeys.member(userId),
    queryFn: () => fetchMember(userId),
  });
  const followingQuery = useQuery({
    queryKey: identityKeys.follow(userId),
    queryFn: () => fetchIsFollowing(userId),
  });
  const mutualsQuery = useQuery({
    queryKey: identityKeys.memberMutuals(userId),
    queryFn: () => fetchMemberMutuals(userId),
  });
  const circlesQuery = useQuery({
    queryKey: ['profile', 'self-circles'] as const,
    queryFn: () => fetchMyCircles({ pageSize: 6 }),
    enabled: viewer !== null && viewer.id === userId,
  });
  const networksQuery = useQuery({
    queryKey: ['profile', 'self-networks'] as const,
    queryFn: () => fetchMyNetworks(),
    enabled: viewer !== null && viewer.id === userId,
  });
  // Public profile post pagination — same shape as the self route. See
  // `routes/_authed/profile/index.tsx` for the limit caps.
  const POSTS_PAGE = 5;
  const POSTS_MAX = 50;
  const [postsLimit, setPostsLimit] = useState<number>(POSTS_PAGE);
  const postsQuery = useQuery({
    queryKey: feedKeys.authorPosts(userId, postsLimit),
    queryFn: () => fetchPostsByAuthor(userId, postsLimit),
    // See the matching note in `/profile/index.tsx`: keep the previous
    // page painted while the next limit fetches, so the page doesn't
    // collapse to skeleton mid-click.
    placeholderData: keepPreviousData,
  });

  // Member identity must resolve before the chrome can paint a name / photo —
  // the chrome skeleton handles the few hundred ms until then.
  const showSkeleton = useDelayedLoading(memberQuery.isLoading || viewer === null);
  if (showSkeleton) return <ProfileChromeSkeleton />;
  if (memberQuery.isLoading || viewer === null) return null;
  // 404 from the member API → branded NotFoundPage. Non-404 errors keep
  // the retry surface. Rendered directly because `throw notFound()` from
  // a useQuery render context goes to the catch-boundary in this version
  // of TanStack Router, not to the 404 surface.
  if (isNotFoundApiError(memberQuery.error)) return <NotFoundPage />;
  if (memberQuery.isError) {
    return (
      <ErrorState
        title="Couldn't load profile"
        body="Something on our end didn't respond. Give it a moment and try again."
        primary={<RetryButton />}
      />
    );
  }
  if (!memberQuery.data) throw notFound();
  const memberData = memberQuery.data;

  const isFollowing = followingQuery.data ?? false;
  const isViewer = viewer.id === memberData.id;

  const header: ProfileHeaderData = {
    id: memberData.id,
    name:
      [memberData.firstName, memberData.lastName]
        .filter(Boolean)
        .join(' ')
        .trim() || 'Member',
    headline: buildHeadline(memberData),
    location: memberData.location ?? '',
    memberSince: memberSinceFromCreatedAt(memberData.createdAt),
    photo: memberData.photoUrl ?? undefined,
    isViewer,
    isFollowing,
  };

  // Body slices: each query resolves independently; ProfileContentView paints
  // skeleton sub-components for any region whose data isn't ready yet.
  const collateralReady =
    !followingQuery.isLoading &&
    !mutualsQuery.isLoading &&
    !postsQuery.isLoading &&
    !(isViewer && (circlesQuery.isLoading || networksQuery.isLoading));

  let profile: Profile | null = null;
  if (collateralReady) {
    const mutuals = mutualsQuery.data ?? null;
    const posts = postsQuery.data ?? [];
    const circles = circlesQuery.data?.items ?? [];
    const networks = networksQuery.data?.items ?? [];

    profile = mapProfileViewToProfile(memberData, isFollowing, {
      circles: circles.map((c) => ({
        id: c.id,
        name: c.name,
        role: c.role === 'leader' ? 'Leader' : 'Member',
        meta: `${c.memberCount} member${c.memberCount === 1 ? '' : 's'}`,
        imageUrl: c.imageUrl,
      })),
      networks: networks.map((n) => ({
        id: n.id,
        name: n.name,
        memberCount: n.memberCount,
        imageUrl: n.imageUrl,
      })),
      posts: posts.map((p) => authoredPostToFeedPost(p, formatRelativeShort)),
      mutuals: (mutuals?.mutualMembers ?? []).slice(0, 6).map((m) => ({
        id: m.id,
        name: `${m.firstName} ${m.lastName}`.trim(),
        photo: m.photoUrl ?? '',
      })),
    });
  }

  const postsFetched = postsQuery.data?.length ?? 0;
  const hasMorePosts = postsFetched >= postsLimit && postsLimit < POSTS_MAX;

  return (
    <ProfileChrome header={header}>
      <ProfileContentView
        profile={profile}
        hasMorePosts={hasMorePosts}
        loadingMorePosts={postsQuery.isFetching}
        onLoadMorePosts={() =>
          setPostsLimit((n) => Math.min(n + POSTS_PAGE, POSTS_MAX))
        }
      />
    </ProfileChrome>
  );
}
